import {
	EVENTS,
	track,
	registerUrlSuperProps,
	setCurrentRoute,
	setCurrentMaker,
	routeRecordKey,
	maskPathSegments,
} from "../utils/analytics"

/**
 * Tier 2 page views. The SDK is initialised with `track_pageview: false`
 * because hub is an SPA — the SDK's own pageview tracking would fire once on
 * boot and never again.
 *
 * `page_route` is the file-based route name (e.g. `orders-id`), the
 * low-cardinality key to group on. `page_path` is the matched ROUTE PATTERN,
 * not the concrete URL — see the comment on the track call.
 */
let leftPreviousPageAt: number | null = null

/** Most specific matched pattern, masked. Falls back to the path on a 404. */
const routePattern = (route: { matched?: { path: string }[]; path: string }): string =>
	maskPathSegments(route.matched?.[route.matched.length - 1]?.path ?? route.path)

export default defineNuxtRouteMiddleware((to, from) => {
	if (!import.meta.client) return
	setCurrentRoute(to.name)
	// A tab, a filter or a sub-page of the same record keeps it open; moving to
	// another record re-fetches, and its chosen-record mutation sets the maker.
	if (!from || routeRecordKey(to) !== routeRecordKey(from)) setCurrentMaker(undefined)
	if (from && to.fullPath === from.fullPath) return

	const now = Date.now()
	const timeOnPrevious = leftPreviousPageAt === null ? undefined : now - leftPreviousPageAt
	leftPreviousPageAt = now

	// Before the event: $identify and other SDK-internal events bypass track(),
	// so only a super property keeps the raw query string off them.
	registerUrlSuperProps()

	track(EVENTS.PAGE_VIEWED, {
		page_route: to.name,
		// The matched route PATTERN ("/password/:id"), never the concrete path.
		// Hub puts secrets in the path — /password/<verification_id> is the link in
		// every reset email, and that id is posted straight back to resetPassword —
		// so to.path sent a live credential to Mixpanel and from.path sent it again
		// as the next page's referrer.
		//
		// Masked even though the pattern is already safe: that keeps the guarantee
		// here rather than in an assumption about what vue-router puts in `matched`,
		// and it normalises Nuxt's "/orders/:id()" to "/orders/:id" for free.
		page_path: routePattern(to),
		referrer_path: from ? routePattern(from) : undefined,
		time_on_previous_page_ms: timeOnPrevious,
	})
})
